/**
 * System admin console (design-reference §4.15) — two clear sections behind a
 * real tablist:
 *
 *   1. **Partners** — a CRUD over the resellers that own tenants: list them, add
 *      one, edit one, and per partner mint a login account (a `partner_admin`
 *      whose password the system admin sets here) alongside that partner's
 *      existing accounts.
 *   2. **System config** — the EXISTING versioned model/prompt/threshold
 *      configuration, unchanged in behaviour: editing it stages a NEW active
 *      version through a rollout confirm, and a prior version can be re-activated
 *      (rollback) from the history.
 *
 * Every call runs against the real backend through the shared, session-aware
 * `api.admin.partners.*` surface.
 */
import { useEffect, useId, useMemo, useState } from 'react';

import {
  Button,
  Card,
  CardBody,
  type Column,
  ConfirmModal,
  DataTable,
  Drawer,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  Select,
  type SelectOption,
  Skeleton,
  Spinner,
  Tabs,
  useToast,
} from '../../components';
import { ApiError } from '../../api/errors';
import type { EntityStatus, ModelPromptConfig, Partner, User } from '../../api/schema';

import { useAdminApi } from './api';
import { fmtDate } from './format';
import { useSystemAdmin, type SystemAdminData } from './useAdmin';

import './admin.css';

const STATUS_OPTIONS: readonly SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function statusLabel(status: EntityStatus): string {
  if (status === 'suspended') return 'Suspended';
  if (status === 'archived') return 'Archived';
  return 'Active';
}

/** Partner/account status as a coloured dot + label (never colour alone). */
function StatusPill({ status }: { status: EntityStatus }): React.JSX.Element {
  const tone = status === 'active' ? 'ok' : 'bad';
  return (
    <span className={`adm-partner-status adm-partner-status--${tone}`}>
      <span className={`adm-dot adm-dot--${tone}`} aria-hidden="true" />
      {statusLabel(status)}
    </span>
  );
}

// ------------------------------------------------------------- partner form ----

interface PartnerFormDrawerProps {
  mode: 'create' | 'edit';
  partner?: Partner;
  onClose: () => void;
  /** Called after a successful create/update so the caller can refresh + close. */
  onDone: () => void;
}

/** Add- or edit-partner drawer: name, default confidence threshold, status. */
function PartnerFormDrawer({ mode, partner, onClose, onDone }: PartnerFormDrawerProps): React.JSX.Element {
  const api = useAdminApi();
  const { toast } = useToast();
  const [name, setName] = useState(partner?.name ?? '');
  const [threshold, setThreshold] = useState(partner?.settings?.default_confidence_threshold ?? 0.75);
  const [status, setStatus] = useState<EntityStatus>(partner?.status ?? 'active');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const thresholdId = useId();

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    const body = { name: trimmed, status, settings: { default_confidence_threshold: threshold } };
    try {
      if (mode === 'create') {
        await api.partners.create(body);
        toast('Partner created');
      } else if (partner !== undefined) {
        await api.partners.update(partner.id, body);
        toast('Partner updated');
      }
      onDone();
    } catch {
      setError(
        mode === 'create'
          ? 'Could not create the partner. Check the name and try again.'
          : 'Could not update the partner. Please try again.',
      );
      setSubmitting(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        onClick={() => {
          void submit();
        }}
        disabled={!canSubmit}
        loading={submitting}
        loadingLabel={mode === 'create' ? 'Creating…' : 'Saving…'}
      >
        {mode === 'create' ? 'Add partner' : 'Save changes'}
      </Button>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      title={mode === 'create' ? 'Add partner' : `Edit ${partner?.name ?? 'partner'}`}
      footer={footer}
    >
      <div className="adm-form">
        <FormField label="Partner name">
          {(p) => (
            <Input
              {...p}
              value={name}
              placeholder="e.g. Vertex Docs"
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </FormField>

        <div className="adm-threshold">
          <label className="adm-field__label adm-threshold__label" htmlFor={thresholdId}>
            Default confidence threshold
          </label>
          <input
            id={thresholdId}
            type="range"
            min="0.5"
            max="0.95"
            step="0.05"
            value={threshold}
            onChange={(e) => {
              setThreshold(Number(e.target.value));
            }}
            className="adm-range"
          />
          <span className="adm-threshold__value">{threshold.toFixed(2)}</span>
        </div>

        <FormField label="Status">
          {(p) => (
            <Select
              {...p}
              value={status}
              options={STATUS_OPTIONS}
              onChange={(e) => {
                setStatus(e.target.value as EntityStatus);
              }}
            />
          )}
        </FormField>

        {error !== null ? (
          <p className="adm-note adm-note--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Drawer>
  );
}

// ------------------------------------------------------------ accounts drawer ---

type AccountsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; accounts: User[] };

/** A partner's login accounts plus an invite-by-email create form. */
function AccountsDrawer({ partner, onClose }: { partner: Partner; onClose: () => void }): React.JSX.Element {
  const api = useAdminApi();
  const { toast } = useToast();
  const [state, setState] = useState<AccountsState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<'email' | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    void (async () => {
      try {
        const accounts = await api.partners.admins.list(partner.id, controller.signal);
        if (controller.signal.aborted) return;
        setState({ status: 'ready', accounts });
      } catch (err) {
        if (controller.signal.aborted || isAbort(err)) return;
        setState({ status: 'error' });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [api, partner.id, nonce]);

  const canSubmit = email.trim().length > 0 && displayName.trim().length > 0 && !submitting;

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setFormError(null);
    setErrorField(null);
    try {
      const account = await api.partners.admins.create(partner.id, {
        email: email.trim(),
        display_name: displayName.trim(),
      });
      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', accounts: [...current.accounts, account] }
          : { status: 'ready', accounts: [account] },
      );
      toast('Account created — an email to set a password has been sent');
      setEmail('');
      setDisplayName('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorField('email');
        setFormError('That email is already in use');
      } else {
        setFormError('Could not create the account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Close
      </Button>
      <Button
        onClick={() => {
          void submit();
        }}
        disabled={!canSubmit}
        loading={submitting}
        loadingLabel="Creating…"
      >
        Create account
      </Button>
    </>
  );

  return (
    <Drawer open onClose={onClose} title={`Accounts · ${partner.name}`} footer={footer}>
      <div className="adm-form">
        <section aria-label="Login accounts">
          <h3 className="adm-eyebrow">Login accounts</h3>
          {state.status === 'loading' ? (
            <div className="adm-accounts__loading">
              <Spinner />
            </div>
          ) : null}
          {state.status === 'error' ? (
            <ErrorState
              title="Couldn't load accounts"
              body="This partner's login accounts failed to load."
              actions={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setNonce((n) => n + 1);
                  }}
                >
                  Retry
                </Button>
              }
            />
          ) : null}
          {state.status === 'ready' && state.accounts.length === 0 ? (
            <p className="adm-muted">No accounts yet — create the first one below.</p>
          ) : null}
          {state.status === 'ready' && state.accounts.length > 0 ? (
            <ul className="adm-accounts">
              {state.accounts.map((account) => (
                <li key={account.id} className="adm-account">
                  <span className="adm-account__name">{account.display_name}</span>
                  <span className="adm-account__email adm-mono">{account.email}</span>
                  <StatusPill status={account.status ?? 'active'} />
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        <section aria-label="Create account" className="adm-form">
          <h3 className="adm-eyebrow">Create account</h3>
          <FormField
            label="Email"
            error={errorField === 'email' && formError !== null ? formError : undefined}
          >
            {(p) => (
              <Input
                {...p}
                type="email"
                value={email}
                autoComplete="off"
                placeholder="admin@partner.com"
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            )}
          </FormField>
          <FormField label="Display name">
            {(p) => (
              <Input
                {...p}
                value={displayName}
                placeholder="Jordan Lee"
                onChange={(e) => {
                  setDisplayName(e.target.value);
                }}
              />
            )}
          </FormField>
          {formError !== null && errorField === null ? (
            <p className="adm-note adm-note--error" role="alert">
              {formError}
            </p>
          ) : null}
        </section>
      </div>
    </Drawer>
  );
}

// ------------------------------------------------------------ partners section --

function PartnersSection({ data, reload }: { data: SystemAdminData; reload: () => void }): React.JSX.Element {
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [accountsFor, setAccountsFor] = useState<Partner | null>(null);

  const columns: readonly Column<Partner>[] = [
    {
      id: 'name',
      header: 'Partner',
      render: (p) => <span className="adm-partner-name">{p.name}</span>,
      sortValue: (p) => p.name.toLowerCase(),
    },
    {
      id: 'threshold',
      header: 'Default threshold',
      render: (p) => (
        <span className="adm-num">{(p.settings?.default_confidence_threshold ?? 0.75).toFixed(2)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (p) => <StatusPill status={p.status ?? 'active'} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (p) => (
        <span className="adm-row-actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditing(p);
            }}
          >
            Edit
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setAccountsFor(p);
            }}
          >
            Create account
          </Button>
        </span>
      ),
    },
  ];

  return (
    <section className="adm-pane" aria-label="Partners">
      <div className="adm-pane__head">
        <div>
          <h2 className="adm-card__title">Partners</h2>
          <p className="adm-card__sub">Resellers that own tenants. Add one, edit it, or create its login accounts.</p>
        </div>
        {data.partners.length > 0 ? (
          <Button
            size="sm"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            Add partner
          </Button>
        ) : null}
      </div>

      {data.partners.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title="Add your first partner"
              body="Partners are the resellers that own tenants. Create one to start onboarding."
              actions={
                <Button
                  onClick={() => {
                    setAddOpen(true);
                  }}
                >
                  Add partner
                </Button>
              }
            />
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          rows={data.partners}
          getRowId={(p) => p.id}
          label="Partners"
          gridTemplate="1.6fr 1fr 1fr 220px"
          initialSort={{ columnId: 'name', dir: 1 }}
        />
      )}

      {addOpen ? (
        <PartnerFormDrawer
          mode="create"
          onClose={() => {
            setAddOpen(false);
          }}
          onDone={() => {
            setAddOpen(false);
            reload();
          }}
        />
      ) : null}

      {editing !== null ? (
        <PartnerFormDrawer
          key={editing.id}
          mode="edit"
          partner={editing}
          onClose={() => {
            setEditing(null);
          }}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}

      {accountsFor !== null ? (
        <AccountsDrawer
          key={accountsFor.id}
          partner={accountsFor}
          onClose={() => {
            setAccountsFor(null);
          }}
        />
      ) : null}
    </section>
  );
}

// -------------------------------------------------------------- config section --

interface DraftConfig {
  ocr_deployment: string;
  llm_model: string;
  classify_prompt_version: string;
  chat_prompt_version: string;
  default_confidence_threshold: number;
}

function toDraft(c: ModelPromptConfig): DraftConfig {
  return {
    ocr_deployment: c.ocr_deployment,
    llm_model: c.llm_model,
    classify_prompt_version: c.classify_prompt_version,
    chat_prompt_version: c.chat_prompt_version,
    default_confidence_threshold: c.default_confidence_threshold,
  };
}

function diff(draft: DraftConfig, base: ModelPromptConfig): Partial<DraftConfig> {
  const out: Partial<DraftConfig> = {};
  if (draft.ocr_deployment !== base.ocr_deployment) out.ocr_deployment = draft.ocr_deployment;
  if (draft.llm_model !== base.llm_model) out.llm_model = draft.llm_model;
  if (draft.classify_prompt_version !== base.classify_prompt_version)
    out.classify_prompt_version = draft.classify_prompt_version;
  if (draft.chat_prompt_version !== base.chat_prompt_version)
    out.chat_prompt_version = draft.chat_prompt_version;
  if (draft.default_confidence_threshold !== base.default_confidence_threshold)
    out.default_confidence_threshold = draft.default_confidence_threshold;
  return out;
}

/** The versioned model/prompt/threshold configuration — unchanged in behaviour. */
function ConfigSection({ data, reload }: { data: SystemAdminData; reload: () => void }): React.JSX.Element {
  const { toast } = useToast();
  const api = useAdminApi();
  const [draft, setDraft] = useState<DraftConfig>(() => toDraft(data.config.settings));
  const [confirmSave, setConfirmSave] = useState(false);
  const [activateVersion, setActivateVersion] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const thresholdId = useId();

  const changes = useMemo(() => diff(draft, data.config.settings), [draft, data.config.settings]);
  const isDirty = Object.keys(changes).length > 0;

  const field = <K extends keyof DraftConfig>(key: K, value: DraftConfig[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const doSave = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.config.update(changes);
      toast('Config staged — rolling out to 1 pilot tenant first, then fleet-wide in 24 h');
      setConfirmSave(false);
      reload();
    } catch {
      toast('Could not stage the configuration change', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const doActivate = async (): Promise<void> => {
    if (activateVersion === null) return;
    setBusy(true);
    try {
      await api.config.activate(activateVersion);
      toast(`Version ${String(activateVersion)} activated — rolled back`);
      setActivateVersion(null);
      reload();
    } catch {
      toast('Could not activate that version', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="adm-card" aria-labelledby="adm-config-h">
      <header className="adm-card__head">
        <div>
          <h2 id="adm-config-h" className="adm-card__title">
            Model &amp; prompt configuration
          </h2>
          <p className="adm-card__sub">
            Active version {data.config.version} · {data.config.active ? 'live' : 'inactive'}
          </p>
        </div>
      </header>

      <div className="adm-config-fields">
        <label className="adm-field__label" htmlFor="cfg-ocr">
          OCR engine
        </label>
        <Input
          id="cfg-ocr"
          value={draft.ocr_deployment}
          onChange={(e) => {
            field('ocr_deployment', e.target.value);
          }}
          className="adm-mono"
        />
        <label className="adm-field__label" htmlFor="cfg-llm">
          Classification model
        </label>
        <Input
          id="cfg-llm"
          value={draft.llm_model}
          onChange={(e) => {
            field('llm_model', e.target.value);
          }}
          className="adm-mono"
        />
        <label className="adm-field__label" htmlFor="cfg-clf">
          Classify prompt version
        </label>
        <Input
          id="cfg-clf"
          value={draft.classify_prompt_version}
          onChange={(e) => {
            field('classify_prompt_version', e.target.value);
          }}
          className="adm-mono"
        />
        <label className="adm-field__label" htmlFor="cfg-chat">
          Chat prompt version
        </label>
        <Input
          id="cfg-chat"
          value={draft.chat_prompt_version}
          onChange={(e) => {
            field('chat_prompt_version', e.target.value);
          }}
          className="adm-mono"
        />

        <div className="adm-threshold">
          <label className="adm-field__label adm-threshold__label" htmlFor={thresholdId}>
            Default confidence threshold
          </label>
          <input
            id={thresholdId}
            type="range"
            min="0.5"
            max="0.95"
            step="0.05"
            value={draft.default_confidence_threshold}
            onChange={(e) => {
              field('default_confidence_threshold', Number(e.target.value));
            }}
            className="adm-range"
          />
          <span className="adm-threshold__value">{draft.default_confidence_threshold.toFixed(2)}</span>
        </div>
      </div>

      <p className="adm-note">
        Tenants can override their own threshold. Model/prompt changes roll out staged: 1 pilot tenant →
        fleet after 24 h of clean metrics.
      </p>

      <Button
        onClick={() => {
          setConfirmSave(true);
        }}
        disabled={!isDirty}
        className="adm-self-start"
      >
        Save configuration
      </Button>

      <div className="adm-history">
        <h3 className="adm-eyebrow">Version history</h3>
        <ul className="adm-history__list">
          {data.history.map((v) => (
            <li key={v.id} className="adm-history__row">
              <span className="adm-history__ver">v{v.version}</span>
              <span className="adm-history__date">{fmtDate(v.created_at)}</span>
              {v.active ? (
                <span className="adm-pill adm-pill--ok">Active</span>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setActivateVersion(v.version);
                  }}
                >
                  Activate
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <ConfirmModal
        open={confirmSave}
        title="Roll out configuration change?"
        confirmLabel="Stage rollout"
        cancelLabel="Cancel"
        loading={busy}
        onCancel={() => {
          setConfirmSave(false);
        }}
        onConfirm={() => {
          void doSave();
        }}
      >
        Safe rollout: the change applies to <strong>1 pilot tenant</strong> first. If classification
        metrics stay clean for 24&nbsp;h, it promotes fleet-wide automatically. You can roll back at any
        point.
      </ConfirmModal>

      <ConfirmModal
        open={activateVersion !== null}
        title="Activate this configuration version?"
        confirmLabel="Activate"
        cancelLabel="Cancel"
        loading={busy}
        onCancel={() => {
          setActivateVersion(null);
        }}
        onConfirm={() => {
          void doActivate();
        }}
      >
        Version {activateVersion} becomes the active configuration the providers read. This is a
        rollback — the current version is kept for re-activation.
      </ConfirmModal>
    </section>
  );
}

// --------------------------------------------------------------------- screen ---

function SystemBody({ data, reload }: { data: SystemAdminData; reload: () => void }): React.JSX.Element {
  const [tab, setTab] = useState('partners');

  const tabs = useMemo(
    () => [
      { id: 'partners', label: 'Partners', content: <PartnersSection data={data} reload={reload} /> },
      { id: 'config', label: 'System config', content: <ConfigSection data={data} reload={reload} /> },
    ],
    [data, reload],
  );

  return (
    <div className="adm-screen">
      <Tabs tabs={tabs} value={tab} onChange={setTab} label="System admin sections" />
    </div>
  );
}

export function SystemScreen(): React.JSX.Element {
  const state = useSystemAdmin();

  if (state.status === 'loading') {
    return (
      <div className="adm-screen" aria-busy="true">
        <Skeleton height={280} />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="adm-screen">
        <ErrorState
          title="Couldn't load system admin"
          body="The partners and platform configuration failed to load."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="secondary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  return <SystemBody data={state.data} reload={state.reload} />;
}
